/**
 * A profile's picture, or its coloured initial when there is none.
 *
 * One component so the picker, the header and the settings page cannot drift apart —
 * they did in the first pass, and a profile that looked different in two places read as
 * two profiles.
 */
import type { ProfileDto } from '@filmodasi/shared';
import { assetUrl } from '../api/client';

/**
 * Where the supplied illustrations live.
 *
 * Built from an id rather than stored as a path, so a profile row can never name an
 * arbitrary URL for the browser to fetch — the same reason the model refuses a remote
 * `avatar`. The server has already checked the id is one it knows.
 *
 * Through `assetUrl` rather than `import.meta.env.BASE_URL` directly: under the §29.2
 * loader the same bundle runs from a `file:` document, where a root-relative `/avatars/…`
 * resolves to the filesystem root and every built-in avatar silently becomes a broken
 * image. This is the only place in the app that builds a URL to its own static files.
 */
export function builtInAvatarSrc(avatarId: string): string {
  return assetUrl(`avatars/${avatarId}.svg`);
}

export function Avatar({
  profile,
  size = 96,
}: {
  profile: Pick<ProfileDto, 'name' | 'avatarColor' | 'avatar'> & { avatarId?: string | null };
  size?: number;
}) {
  const style = { width: size, height: size, fontSize: size * 0.4 };

  // An upload wins over a picked illustration: it is the more specific choice, and it is
  // the one that had to be made deliberately.
  if (!profile.avatar && profile.avatarId) {
    return (
      <img className="avatar avatar--img" style={style} src={builtInAvatarSrc(profile.avatarId)} alt="" />
    );
  }

  if (profile.avatar) {
    return (
      <img
        className="avatar avatar--img"
        style={style}
        src={profile.avatar}
        // Decorative: the name is always rendered next to it, so alt text would be read
        // twice by a screen reader.
        alt=""
      />
    );
  }

  return (
    <span className="avatar" style={{ ...style, background: profile.avatarColor }}>
      {/* Intl-aware: `slice(0,1)` splits an emoji or a surrogate pair down the middle. */}
      {[...profile.name][0]?.toLocaleUpperCase('tr') ?? '?'}
    </span>
  );
}
